import { AHText, AHTextFonts, Box, useScaleFactor, useSpaceSize } from "arkh";
import React, { PropsWithChildren, useMemo } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import { graphql, useFragment } from "react-relay";

import { WidgetEmployeeScheduleShiftBar } from "./WidgetEmployeeScheduleShiftBar";

import { WidgetEmployeeScheduleShiftsEmployeeNamePanel_employee$key } from "./__generated__/WidgetEmployeeScheduleShiftsEmployeeNamePanel_employee.graphql";
import { WidgetEmployeeScheduleShiftsEmployee_employee$key } from "./__generated__/WidgetEmployeeScheduleShiftsEmployee_employee.graphql";

export const Employee = React.memo(EmployeeCmp);
function EmployeeCmp(props: {
  employee: WidgetEmployeeScheduleShiftsEmployee_employee$key;
  isDoubleHeight: boolean;
  listItemHeight: number;
  employeePaneWidth: number;
  dayStartMinutes: number;
  boxWidth: number;

  showTime: boolean;
}) {
  const employee = useFragment(
    graphql`
      fragment WidgetEmployeeScheduleShiftsEmployee_employee on Employee {
        id
        ...WidgetEmployeeScheduleShiftsEmployeeNamePanel_employee
        shifts {
          id
          ...WidgetEmployeeScheduleShiftBar
        }
      }
    `,
    props.employee
  );

  return (
    <View
      style={{
        height: props.listItemHeight,
        justifyContent: "center",
        width: "100%",
      }}
    >
      <NamePanel
        employeePaneWidth={props.employeePaneWidth}
        isDoubleHeight={props.isDoubleHeight}
        employee={employee}
      />

      <ShiftCanvas
        employeePaneWidth={props.employeePaneWidth}
        isDoubleHeight={props.isDoubleHeight}
        hide={!props.showTime}
      >
        {employee.shifts.map((shift, i) => (
          <WidgetEmployeeScheduleShiftBar
            key={shift.id}
            shift={shift}
            boxWidth={props.boxWidth}
            isDoubleHeight={props.isDoubleHeight}
            showTime
            dayStartMinutes={props.dayStartMinutes}
          />
        ))}
      </ShiftCanvas>
      <ShiftCanvas
        employeePaneWidth={props.employeePaneWidth}
        isDoubleHeight={props.isDoubleHeight}
        hide={props.showTime}
      >
        {employee.shifts.map((shift) => (
          <WidgetEmployeeScheduleShiftBar
            key={shift.id + "TEAM"}
            shift={shift}
            boxWidth={props.boxWidth}
            isDoubleHeight={props.isDoubleHeight}
            showTime={false}
            dayStartMinutes={props.dayStartMinutes}
          />
        ))}
      </ShiftCanvas>
    </View>
  );
}

function ShiftCanvas(
  props: PropsWithChildren<{
    employeePaneWidth: number;
    isDoubleHeight: boolean;
    hide: boolean;
  }>
) {
  const sf = useScaleFactor();
  const spaceLeft = useSpaceSize("xsmall");
  const style: StyleProp<ViewStyle> = useMemo(
    () => ({
      overflow: "hidden",
      height: props.isDoubleHeight ? 30 * sf : 17 * sf,
      justifyContent: "center",
      left: props.employeePaneWidth,
      paddingLeft: spaceLeft,
      width: "100%",
      position: "absolute",
      opacity: props.hide ? 0 : 1,
    }),
    [props.employeePaneWidth, props.hide, props.isDoubleHeight, sf, spaceLeft]
  );
  return <View style={style}>{props.children}</View>;
}

const NamePanel = React.memo(NamePanelCmp);
function NamePanelCmp(props: {
  isDoubleHeight: boolean;
  employeePaneWidth: number;
  employee: WidgetEmployeeScheduleShiftsEmployeeNamePanel_employee$key;
}) {
  const sf = useScaleFactor();
  const employee = useFragment(
    graphql`
      fragment WidgetEmployeeScheduleShiftsEmployeeNamePanel_employee on Employee {
        name
      }
    `,
    props.employee
  );

  const name = useMemo(() => {
    if (props.isDoubleHeight) {
      const parts = employee.name.split(" ");
      const firstName = parts[0];
      const lastName = parts.slice(1).join(" ");
      return `${firstName}\n${lastName}`.trim();
    }
    return employee.name;
  }, [props.isDoubleHeight, employee.name]);
  return (
    <Box
      style={{
        justifyContent: "center",
        width: props.employeePaneWidth,
      }}
    >
      <AHText
        font={AHTextFonts.BOLD}
        numberOfLines={props.isDoubleHeight ? 2 : 1}
        spaceLeft="xsmall"
        style={{ fontSize: Math.round(12 * sf) }}
        type="detail"
        ellipsizeMode="tail"
        color="darkerText"
      >
        {name}
      </AHText>
    </Box>
  );
}
